/**
 * Sources Page
 *
 * Displays article sources (domains) with article counts, unread counts,
 * and quality scores. Supports sorting by count or quality score.
 * Clicking a source navigates to a dedicated source articles page.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageLoading from '../components/PageLoading';
import EmptyState from '../components/EmptyState';
import { getArticleSources } from '../services/feeds';
import Pagination from '../components/Pagination';
import { formatDateCompact } from '../utils/dateUtils';

const PER_PAGE = 30;

const Sources = () => {
  const [sources, setSources] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, total_pages: 1 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('score');
  const navigate = useNavigate();

  const fetchSources = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const data = await getArticleSources({ page, per_page: PER_PAGE, sort_by: sortBy });
      setSources(data.sources || []);
      setPagination({
        total: data.total || 0,
        page: data.page || 1,
        total_pages: data.total_pages || 1,
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [sortBy]);

  useEffect(() => {
    fetchSources(1);
  }, [fetchSources]);

  const handlePageChange = (page) => {
    fetchSources(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSourceClick = (domain) => {
    navigate(`/sources/${encodeURIComponent(domain)}`);
  };

  const handleSortChange = (newSort) => {
    if (newSort !== sortBy) {
      setSortBy(newSort);
    }
  };

  const getDomainColor = (domain) => {
    let hash = 0;
    for (let i = 0; i < domain.length; i++) {
      hash = domain.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
      'border-l-fun-coral',
      'border-l-fun-sky',
      'border-l-fun-mint',
      'border-l-fun-lemon',
      'border-l-fun-pink',
      'border-l-fun-peach',
      'border-l-fun-lavender',
    ];
    return colors[Math.abs(hash) % colors.length];
  };

  const formatScore = (score) => {
    if (score == null) return null;
    // Show as score out of 10 (e.g. 5.0) rather than percentage
    return (score * 10).toFixed(1);
  };

  if (isLoading && sources.length === 0) {
    return (
      <PageLoading message="Loading sources..." />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-clay-bg flex items-center justify-center">
        <div className="card-clay text-center p-8">
          <div className="text-fun-coral mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="font-display text-xl font-semibold text-heading mb-2">Failed to Load Sources</h2>
          <p className="text-secondary-text mb-4">{error}</p>
          <button onClick={() => fetchSources(1)} className="btn-clay-accent">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <div className="bg-surface shadow-clay border-b-3 border-clay-border">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to="/dashboard"
                className="mr-4 p-2 text-tertiary-text hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-clay transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div>
                <h1 className="font-display text-2xl font-bold text-heading">Sources</h1>
                <p className="text-sm text-secondary-text mt-1">
                  {pagination.total} {pagination.total === 1 ? 'source' : 'sources'}
                </p>
              </div>
            </div>

            {/* Sort Toggle */}
            <div className="flex items-center space-x-1 bg-surface-hover rounded-clay p-1">
              <button
                onClick={() => handleSortChange('article_count')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                  sortBy === 'article_count'
                    ? 'bg-surface text-heading shadow-sm'
                    : 'text-secondary-text hover:text-body-text'
                }`}
              >
                Count
              </button>
              <button
                onClick={() => handleSortChange('score')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                  sortBy === 'score'
                    ? 'bg-surface text-heading shadow-sm'
                    : 'text-secondary-text hover:text-body-text'
                }`}
              >
                Quality
              </button>
              <button
                onClick={() => handleSortChange('unread_count')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                  sortBy === 'unread_count'
                    ? 'bg-surface text-heading shadow-sm'
                    : 'text-secondary-text hover:text-body-text'
                }`}
              >
                Unread
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {sources.length === 0 ? (
          <EmptyState
            icon={
              <svg className="w-16 h-16 text-tertiary-text mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="No Sources Yet"
            description="Save some articles and your sources will appear here."
            action={{ label: 'Go to Dashboard', to: '/dashboard' }}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sources.map((source) => {
                const scorePercent = formatScore(source.quality_score);
                return (
                  <button
                    key={source.domain}
                    onClick={() => handleSourceClick(source.domain)}
                    className={`card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 text-left border-l-4 ${getDomainColor(source.domain)}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-heading truncate flex-1 mr-2">
                        {source.display_name}
                      </h3>
                      {scorePercent != null && (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                            source.quality_score >= 0.7
                              ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
                              : source.quality_score >= 0.4
                                ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200'
                                : 'bg-surface-hover text-secondary-text'
                          }`}
                        >
                          {scorePercent}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-secondary-text">
                        {source.article_count} {source.article_count === 1 ? 'article' : 'articles'}
                      </span>
                      {source.unread_count > 0 && (
                        <span className="badge-fun-accent text-xs">
                          {source.unread_count} unread
                        </span>
                      )}
                    </div>
                    {/* Engagement stats row */}
                    {(source.total_highlights > 0 || source.total_positive_feedback > 0) && (
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-tertiary-text">
                        {source.total_highlights > 0 && (
                          <span title="Highlights">
                            <svg className="w-3.5 h-3.5 inline mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            {source.total_highlights}
                          </span>
                        )}
                        {source.total_positive_feedback > 0 && (
                          <span title="Positive feedback">
                            <svg className="w-3.5 h-3.5 inline mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                            </svg>
                            {source.total_positive_feedback}
                          </span>
                        )}
                      </div>
                    )}
                    {source.latest_article_at && (
                      <p className="text-xs text-tertiary-text mt-2">
                        Latest: {formatDateCompact(source.latest_article_at)}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>

            {pagination.total_pages > 1 && (
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.total_pages}
                totalItems={pagination.total}
                itemsPerPage={PER_PAGE}
                onPageChange={handlePageChange}
                isLoading={isLoading}
                className="mt-8"
                itemLabel="sources"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Sources;
